'use strict';

import { updateUIFromShadowRoot } from '@core/i18n/window-language';
import { globalSharedStyles, tooltipsStyles, typographySharedStyles } from '@core/styles';
import { UPDATE_LANGUAGE_EVENT } from '@core/constants/EVENTS';
import {
    ACTIVE_ID_ATTRIBUTE,
    CUSTOM_FLEX_GROW_ATTRIBUTE,
    ZOOM_IN_ATTRIBUTE,
} from '@core/constants/ATTRIBUTES';
import { ripple } from '@core/utils/ripple';
import {
    TOOLTIP_CONTENT_WIDTH_ATTRIBUTE,
    TOOLTIP_POSITION_ATTRIBUTE,
    TOOLTIP_POSITION,
} from '@components/TooltipComponent/CONSTANTS';

import { ZOOM_MODAL_ICON_WEB_COMPONENT } from './CONSTANTS';
import {
    updateTooltipContent,
    applyTooltipPositioning,
    handleZoomModalClick,
} from './utils';
import htmlContent from './ZoomModalIconComponent.html';

/**
 * ZoomModalIconComponent
 *
 * A web component that provides a zoom modal icon with tooltip functionality.
 * Handles zoom modal operations and provides visual feedback.
 *
 * @class ZoomModalIconComponent
 * @extends {HTMLElement}
 */
class ZoomModalIconComponent extends HTMLElement {
    static get observedAttributes() {
        return [
            CUSTOM_FLEX_GROW_ATTRIBUTE.toLowerCase(),
            TOOLTIP_CONTENT_WIDTH_ATTRIBUTE.toLowerCase(),
            ACTIVE_ID_ATTRIBUTE.toLowerCase(),
            TOOLTIP_POSITION_ATTRIBUTE.toLowerCase(),
            ZOOM_IN_ATTRIBUTE.toLowerCase(),
        ];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            tooltipsStyles,
            typographySharedStyles,
        ];
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize properties
        this._customFlexGrow = 0;
        this._tooltipContentWidth = 0;
        this._activeId = null;
        this._tooltipPosition = TOOLTIP_POSITION.BOTTOM_RIGHT;
        this._zoomIn = false;

        // Cache DOM references
        this._tooltipIcon = null;
        this._tooltipDropdownContent = null;
        this._tooltipDropdownContentText = null;
        this._tooltipImgsContentContainer = null;
        this._mdIcon = null;

        // Bind event handlers
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleClickBound = this._handleClick.bind(this);
    }

    /**
     * Called when the element is connected to the DOM
     * @returns {void}
     * @override
     */
    connectedCallback() {
        this._cacheDOMElements();
        this._addEventListeners();
        this._addRippleBehavior();
        this.render();
    }

    /**
     * Called when the element is disconnected from the DOM
     * @returns {void}
     * @override
     */
    disconnectedCallback() {
        this._removeEventListeners();
    }

    /**
     * Called when observed attributes change
     * @param {string} name - The attribute name
     * @param {string} oldValue - The old attribute value
     * @param {string} newValue - The new attribute value
     * @returns {void}
     * @override
     */
    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case CUSTOM_FLEX_GROW_ATTRIBUTE.toLowerCase():
                this._customFlexGrow = Number(newValue) || 0;
                break;
            case TOOLTIP_CONTENT_WIDTH_ATTRIBUTE.toLowerCase():
                this._tooltipContentWidth = Number(newValue) || 0;
                break;
            case ACTIVE_ID_ATTRIBUTE.toLowerCase():
                this._activeId = newValue;
                break;
            case TOOLTIP_POSITION_ATTRIBUTE.toLowerCase():
                this._tooltipPosition = newValue;
                break;
            case ZOOM_IN_ATTRIBUTE.toLowerCase():
                this._zoomIn = newValue === 'true';
                break;
        }

        this.render(false);
    }

    /**
     * Cache DOM element references for better performance
     * @returns {void}
     * @private
     */
    _cacheDOMElements() {
        this._tooltipIcon = this.shadowRoot.querySelector('.tooltip-icon');
        this._tooltipDropdownContent = this.shadowRoot.getElementById(
            'tooltip-zoom-modal-content',
        );
        this._tooltipDropdownContentText = this.shadowRoot.querySelector(
            '#tooltip-zoom-modal-content > p',
        );
        this._tooltipImgsContentContainer = this.shadowRoot.querySelector(
            '[data-zoom-modal-tooltip-dropdown]',
        );
        this._mdIcon = this.shadowRoot.querySelector('md-icon');
    }

    /**
     * Render the component
     * @param {boolean} firstTime - Whether this is the first render
     * @returns {void}
     */
    render(firstTime = true) {
        if (
            !this._tooltipDropdownContent ||
            !this._tooltipDropdownContentText ||
            !this._tooltipImgsContentContainer
        ) {
            return;
        }

        // Apply tooltip content width
        if (this._tooltipContentWidth) {
            this._tooltipDropdownContent.style.maxWidth = `${this._tooltipContentWidth}px`;
        }

        // Update tooltip content
        updateTooltipContent(this._tooltipDropdownContentText);

        // Apply tooltip positioning
        applyTooltipPositioning(
            this._tooltipImgsContentContainer,
            this._tooltipPosition,
            this._customFlexGrow,
            this._zoomIn,
        );

        // Add click handler only on first render
        if (firstTime && this._tooltipIcon) {
            this._tooltipIcon.addEventListener('click', this._handleClickBound);
        }

        if (this._zoomIn) {
            this._mdIcon.style.fontSize = '16px';
        } else {
            this._mdIcon.style.fontSize = '24px';
        }
    }

    /**
     * Handle click events on the tooltip icon
     * @param {Event} e - The click event
     * @returns {void}
     * @private
     */
    _handleClick(e) {
        e.preventDefault();
        e.stopPropagation();

        handleZoomModalClick(this._activeId);
    }

    /**
     * Translate the component elements
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        updateUIFromShadowRoot(this.shadowRoot);
    }

    /**
     * Add event listeners to the component elements
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
    }

    /**
     * Remove event listeners to prevent memory leaks
     * @returns {void}
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );

        if (this._tooltipIcon) {
            this._tooltipIcon.removeEventListener('click', this._handleClickBound);
        }
    }

    /**
     * Add the ripple effect to elements with data-ripple attribute
     * @returns {void}
     * @private
     */
    _addRippleBehavior() {
        const rippleElements = this.shadowRoot.querySelectorAll('[data-ripple]');
        rippleElements.forEach((element) => {
            ripple(element);
        });
    }
}

customElements.define(ZOOM_MODAL_ICON_WEB_COMPONENT, ZoomModalIconComponent);
